import _ from "lodash";
import { estimatePlayEngineBitmapFontsBytes } from "./playEngineAssetUtils";
import { buildEffectiveGameConfig } from "./localizationOverrides";
import { fetchExportSizePlan, isAssetExcludedFromExportEstimate } from "./exportSizePlanApi";

const base64SizeMultiplier = 1.37;

export const MAX_PLAYABLE_SIZE_MB = 5;
export const MAX_PLAYABLE_SIZE_BYTES = MAX_PLAYABLE_SIZE_MB * 1024 * 1024;

/** Bytes to subtract from defaultPlayableSize when export omits a runtime lib. */
function getExportRuntimeDeductionBytes(exportSizePlan, selectedBase) {
  if (!exportSizePlan) return { total: 0, spine: 0, quarks: 0 };

  const cfg = _.get(selectedBase, "exportSizeRuntimeDeductionsKb", {});
  let spine = 0;
  let quarks = 0;

  if (exportSizePlan.usesSpineRuntime === false && cfg.spine != null) {
    spine = Number(cfg.spine) * 1024;
  }
  if (exportSizePlan.usesQuarksRuntime === false && cfg.quarks != null) {
    quarks = Number(cfg.quarks) * 1024;
  }

  return { total: spine + quarks, spine, quarks };
}

function isDisabled(isEnabledValue) {
  if (isEnabledValue === false) return true;
  if (_.isPlainObject(isEnabledValue)) {
    return Object.values(isEnabledValue).every((v) => v === false);
  }
  return false;
}

function isSelectedCheerBoardComponent(componentId, selectedRibbon) {
  const ribbonMapping = {
    "Ribbon Type 1": "cheerBoard1",
    "Ribbon Type 2": "cheerBoardWithTexture1",
    "Ribbon Type 3": "cheerBoardWithAtlas1",
  };

  const expectedComponentId = ribbonMapping[selectedRibbon];
  return componentId === expectedComponentId;
}

function getLevelCount(components) {
  for (const [id, comp] of Object.entries(components)) {
    if (id.startsWith("levelSelector")) {
      if (comp.isEnabled === false) return 1;
      if (comp.levelCount) return comp.levelCount;
    }
  }
  for (const [id, comp] of Object.entries(components)) {
    if (id.startsWith("townMatchMap")) {
      if (comp.levelCount) return comp.levelCount;
    }
  }
  return null;
}

function isComponentEffectivelyEnabled(componentId, components, options) {
  const visited = new Set();
  let currentId = componentId;
  let childId = null;

  const cheerBoardComponentIds = ["cheerBoard1", "cheerBoardWithTexture1", "cheerBoardWithAtlas1"];

  if (cheerBoardComponentIds.includes(componentId)) {
    const cheerBoardSelector = components["cheerBoardSelector1"] || components["cheerboardSelector1"];
    if (cheerBoardSelector) {
      const selectedRibbon = cheerBoardSelector.selectedCheerBoard;
      if (!isSelectedCheerBoardComponent(componentId, selectedRibbon)) {
        return false;
      }
    }
  }

  while (currentId) {
    if (visited.has(currentId)) return true;
    visited.add(currentId);

    const component = components[currentId];
    if (!component) break;

    if (component.type === "endCard") {
      const isEndCardEnabled = _.has(options, "isEndCardEnabled") && options.isEndCardEnabled;
      const isGoToMarketEnabled = _.has(options, "isGoToMarketEnabled") && options.isGoToMarketEnabled;
      const showEndCardAfterMarket = !_.has(options, "showEndCardAfterMarket") || options.showEndCardAfterMarket;
      const isEnabled = isEndCardEnabled || (isGoToMarketEnabled && showEndCardAfterMarket);

      if (isEnabled === false) {
        return isEnabled;
      }
    } else if (_.has(component, "isEnabled")) {
      if (isDisabled(component.isEnabled)) {
        return false;
      }
    }

    if ((component.type === "iqCard" || component.type === "flexIQCard") && _.has(component, "isCardEnabled")) {
      if (isDisabled(component.isCardEnabled)) {
        return false;
      }
    }

    const componentType = _.toLower(component.type);
    if (componentType === "cheerboardselector" && childId) {
      const selectedRibbon = component.selectedCheerBoard;
      const isSelectedRibbon = isSelectedCheerBoardComponent(childId, selectedRibbon);
      if (!isSelectedRibbon) {
        return false;
      }
    }

    childId = currentId;
    currentId = component.parentId;
  }

  const targetComponent = components[componentId];
  if (targetComponent && targetComponent.levelConfigs && componentId.match(/^objectType\d+$/)) {
    const levelCount = getLevelCount(components);
    if (levelCount) {
      let hasObjects = false;
      for (let m = 1; m <= levelCount; m++) {
        const levelConfig = targetComponent.levelConfigs[`level${m}`];
        if (levelConfig && levelConfig.objectCount > 0) {
          hasObjects = true;
          break;
        }
      }
      if (!hasObjects) return false;
    }
  }

  return true;
}

function isAssetEnabledByCount(assetGroupKey, levelCount, components, componentId) {
  if (!levelCount) return true;

  const levelSuffixMatch = assetGroupKey.match(/_level(\d+)/);
  const levelPrefixMatch = assetGroupKey.match(/^level(\d+)$/);
  const levelPreviewMatch = assetGroupKey.match(/^levelPreview(\d+)$/);
  const objectsLevelMatch = assetGroupKey.match(/^objects(\d+)$/);

  let levelNumber = null;
  if (levelSuffixMatch) {
    levelNumber = parseInt(levelSuffixMatch[1]);
  } else if (levelPrefixMatch) {
    levelNumber = parseInt(levelPrefixMatch[1]);
  } else if (levelPreviewMatch) {
    levelNumber = parseInt(levelPreviewMatch[1]);
  } else if (objectsLevelMatch) {
    levelNumber = parseInt(objectsLevelMatch[1]);
  }

  if (levelNumber !== null && levelNumber > levelCount) {
    return false;
  }

  const overlayWithLevelMatch = assetGroupKey.match(/^overlay(\d+)_level(\d+)$/);
  const overlayLevel1Match = assetGroupKey.match(/^overlay(\d+)$/);
  let overlayNumber = null;
  let overlayLevelNumber = null;

  if (overlayWithLevelMatch) {
    overlayNumber = parseInt(overlayWithLevelMatch[1]);
    overlayLevelNumber = parseInt(overlayWithLevelMatch[2]);
  } else if (overlayLevel1Match) {
    overlayNumber = parseInt(overlayLevel1Match[1]);
    overlayLevelNumber = 1;
  }

  if (overlayNumber !== null && componentId) {
    const comp = components[componentId];
    if (comp && comp.levelConfigs) {
      if (_.get(comp, `levelConfigs.level${overlayLevelNumber}.isEnabled`) === false) {
        return false;
      }
      const overlayCount = _.get(comp, `levelConfigs.level${overlayLevelNumber}.overlayCount`);
      if (overlayCount != null && overlayNumber > overlayCount) {
        return false;
      }
    }
  }

  if (componentId) {
    const objectMatch = assetGroupKey.match(/^object(\d+)$/);
    if (objectMatch) {
      const objectNumber = parseInt(objectMatch[1]);
      const comp = components[componentId];
      if (comp && comp.levelConfigs && componentId.match(/^objectType\d+$/)) {
        let maxObjectCount = 0;
        for (let m = 1; m <= levelCount; m++) {
          const levelConfig = comp.levelConfigs[`level${m}`];
          if (levelConfig && levelConfig.objectCount > maxObjectCount) {
            maxObjectCount = levelConfig.objectCount;
          }
        }
        if (objectNumber > maxObjectCount) return false;
      }
    }
  }

  return true;
}

const assetFetchTypes = [
  { key: "selectedImages", folder: "images", type: "image" },
  { key: "selectedAtlases", folder: "atlases", type: "atlas" },
  { key: "selectedSpines", folder: "spines", type: "spine" },
  { key: "selectedSounds", folder: "sounds", type: "sound" },
  { key: "selectedFonts", folder: "fonts", type: "font" },
  { key: "selectedGltfs", folder: "glbs", type: "glb" },
];

/**
 * Estimate exported playable size for a version.
 * When preferredLanguage is a non-en localizationOverrides language, uses the effective
 * components for that language (asset slots + isEnabled overrides), matching single-lang export.
 *
 * @param {object} version - real version (English components + localizationOverrides)
 * @param {object} selectedBase
 * @param {object} [assetEnabledByComponent]
 * @param {string} [preferredLanguage] - Localization-tab / export language (default: en)
 */
export async function calculateVersionPlayableSize(
  version,
  selectedBase,
  assetEnabledByComponent,
  preferredLanguage
) {
  if (_.isNil(version)) {
    return { totalSize: 0, componentSizes: [] };
  }

  let totalSizeResult = 0;
  const componentSizes = [];
  const promises = [];
  const seenFiles = new Set();
  const effectiveGameConfig = buildEffectiveGameConfig(_.get(version, "gameConfig"), preferredLanguage);
  const components = _.get(effectiveGameConfig, "components", {});
  const options = _.get(effectiveGameConfig, "options", {});
  const levelCount = getLevelCount(components);
  const enabledByComponentMap = assetEnabledByComponent || {};

  let exportSizePlan = null;
  const baseId = _.get(selectedBase, "_id.$oid") || _.get(selectedBase, "_id");
  if (_.get(selectedBase, "useExportBuildPlanForSizeEstimate") === true && baseId) {
    try {
      exportSizePlan = await fetchExportSizePlan(String(baseId), effectiveGameConfig);
    } catch (err) {
      console.warn("export size plan unavailable; falling back to full asset count", err);
    }
  }

  function isAssetEnabledByComponent(componentId, assetKey) {
    const mapping = enabledByComponentMap[componentId];
    if (!mapping || !mapping[assetKey]) return true;
    return isComponentEffectivelyEnabled(mapping[assetKey], components, options);
  }

  function addFetchedAsset(componentId, id, asset, blob, type) {
    let name = `${componentId} - ${id}`;
    const size = blob.size * base64SizeMultiplier;

    if (_.has(asset, "variant")) {
      name += ` - ${_.get(asset, "variant")}`;
    }

    const index = componentSizes.indexOf((assetSize) => assetSize.name === name);
    if (index !== -1) {
      const existing = componentSizes[index];
      _.set(componentSizes[index], "size", existing.size + blob.size);
    } else {
      componentSizes.push({ id: componentSizes.length, name, size, type });
    }
    totalSizeResult += size;
  }

  Object.entries(components).forEach(([componentId, component]) => {
    if (!isComponentEffectivelyEnabled(componentId, components, options)) return;

    assetFetchTypes.forEach(({ key, folder, type }) => {
      if (!_.has(component, key)) return;
      const selectedAssets = _.get(component, key);
      Object.entries(selectedAssets).forEach(([id, assets]) => {
        if (!isAssetEnabledByCount(id, levelCount, components, componentId)) return;
        if (!isAssetEnabledByComponent(componentId, id)) return;
        assets.forEach((asset) => {
          if (!asset.file || seenFiles.has(asset.file)) return;
          if (isAssetExcludedFromExportEstimate(asset, exportSizePlan)) return;
          seenFiles.add(asset.file);
          const assetPromise = fetch(`${folder}/${asset.file}`)
            .then((response) => response.blob())
            .then((blob) => addFetchedAsset(componentId, id, asset, blob, type));
          promises.push(assetPromise);
        });
      });
    });

    if (_.has(component, "selectedVideos")) {
      const selectedVideos = _.get(component, "selectedVideos");
      Object.entries(selectedVideos).forEach(([id, videos]) => {
        if (!isAssetEnabledByCount(id, levelCount, components, componentId)) return;
        if (!isAssetEnabledByComponent(componentId, id)) return;
        videos.forEach((video) => {
          if (video.variant === "path") {
            if (!video.file || seenFiles.has(video.file)) return;
            if (isAssetExcludedFromExportEstimate(video, exportSizePlan)) return;
            seenFiles.add(video.file);
            const videoPromise = fetch(`videos/${video.file}`)
              .then((response) => response.blob())
              .then((blob) => addFetchedAsset(componentId, id, video, blob, "video"));
            promises.push(videoPromise);
          } else if (video.variant === "url") {
            let name = `${componentId} - ${id}`;
            if (_.has(video, "variant")) {
              name += ` - ${_.get(video, "variant")}`;
            }
            componentSizes.push({ id: componentSizes.length, name, size: null, type: "external video" });
          }
        });
      });
    }
  });

  await Promise.all(promises);

  const playEngineConfig = _.get(version, "playEngineConfig");
  if (playEngineConfig) {
    const { total, perAsset } = estimatePlayEngineBitmapFontsBytes(playEngineConfig);
    Object.entries(perAsset).forEach(([assetKey, info]) => {
      if (!info.bytes) return;
      componentSizes.push({
        id: componentSizes.length,
        name: `bitmap font: ${assetKey} (${info.chars} chars)`,
        size: info.bytes * base64SizeMultiplier,
        type: "bitmap-font",
      });
    });
    totalSizeResult += total * base64SizeMultiplier;
  }

  let defaultPlayableSize;
  if (_.isNil(_.get(selectedBase, "defaultPlayableSizeInMb"))) {
    defaultPlayableSize = 1.4 * 1024 * 1024;
  } else {
    defaultPlayableSize = selectedBase.defaultPlayableSizeInMb * 1024 * 1024;
  }
  componentSizes.push({
    id: componentSizes.length,
    name: "Default playable size",
    size: defaultPlayableSize,
    type: "default",
  });
  totalSizeResult += defaultPlayableSize;

  const runtimeDeduction = getExportRuntimeDeductionBytes(exportSizePlan, selectedBase);
  if (runtimeDeduction.spine > 0) {
    componentSizes.push({
      id: componentSizes.length,
      name: "Spine runtime (excluded from export)",
      size: -runtimeDeduction.spine,
      type: "runtime-adjustment",
    });
    totalSizeResult -= runtimeDeduction.spine;
  }
  if (runtimeDeduction.quarks > 0) {
    componentSizes.push({
      id: componentSizes.length,
      name: "Quarks runtime (excluded from export)",
      size: -runtimeDeduction.quarks,
      type: "runtime-adjustment",
    });
    totalSizeResult -= runtimeDeduction.quarks;
  }

  return { totalSize: totalSizeResult, componentSizes };
}
